import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { CustomCalendar } from "@/components/CustomCalendar/CustomCalendar";
import { type CalendarPagerBounds, useCalendarPager } from "@/components/CustomCalendar/useCalendarPager";
import { WeekStartDaySelector } from "@/components/WeekStartDaySelector/WeekStartDaySelector";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { NEUTRAL } from "@/theme/tokens";
import { PullToRefresh } from "@/ui/PullToRefresh/PullToRefresh";
import type { OpenShift, TransformedShift } from "@/utils/models";
import { useScheduleListSync } from "../useScheduleListSync";
import { ShiftWeekListMedical } from "./medicalDepartment/ShiftWeekListMedical";
import { ScheduleListFooter } from "./ScheduleListFooter";
import { ShiftWeekList } from "./ShiftWeekList";

type NurseScheduleSectionProps = {
	shifts: TransformedShift[];
	/** The days the server sent a schedule for. Undefined while it loads. */
	scheduleBounds?: CalendarPagerBounds;
	allOpenShifts?: Record<string, OpenShift[]>;
	staffScheduleDisplayUntilDate?: DateTime;
	onRefresh?: () => Promise<void>;
	children?: ReactNode;
};

export const NurseScheduleSection: FC<NurseScheduleSectionProps> = ({
	shifts,
	scheduleBounds,
	allOpenShifts,
	staffScheduleDisplayUntilDate,
	onRefresh,
	children,
}) => {
	const { isMedicalDepartment } = useDepartmentFlags();
	const { track } = useAnalytics();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(true);
	const [expandedShiftIds, setExpandedShiftIds] = useState<string[]>([]);
	const [calendarHeaderHeight, setCalendarHeaderHeight] = useState<number>(0);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const childrenContainerRef = useRef<HTMLDivElement | null>(null);

	// Paging stops at the last published day as well as at the end of the schedule the server sent.
	const pagerBounds = useMemo<CalendarPagerBounds | undefined>(
		() =>
			scheduleBounds && {
				firstDay: scheduleBounds.firstDay,
				lastDay:
					staffScheduleDisplayUntilDate && staffScheduleDisplayUntilDate < scheduleBounds.lastDay ? staffScheduleDisplayUntilDate : scheduleBounds.lastDay,
			},
		[scheduleBounds, staffScheduleDisplayUntilDate],
	);
	const pager = useCalendarPager(pagerBounds);
	const { pageStart, pageEnd, selection, selectDate } = pager;
	// The list shows the weeks the calendar shows, up to the last day staff can see.
	const pageShifts = useMemo(() => {
		const listEnd = pagerBounds && pagerBounds.lastDay < pageEnd ? pagerBounds.lastDay : pageEnd;
		return shifts.filter((shift) => shift.date >= pageStart && shift.date < listEnd.startOf("day").plus({ days: 1 }));
	}, [shifts, pageStart, pageEnd, pagerBounds]);
	const listScrollHandlers = useScheduleListSync(scrollRef, pager, pageShifts);
	const selectedDateKey = selection.date.toFormat("yyyy-MM-dd");

	const handleDateSelect = (date: string): void => {
		selectDate(DateTime.fromISO(date));
		setExpandedShiftIds([]);
		setIsCalendarOpen(true);
		track("nurse_schedule_date_selected", { date });
	};

	const handleExpandClick = useCallback((shiftId: string): void => {
		setExpandedShiftIds((prev) => (prev.includes(shiftId) ? prev.filter((id) => id !== shiftId) : [...prev, shiftId]));
	}, []);

	const handleCalendarChange = (newDate: DateTime): void => {
		track("nurse_schedule_calendar_date_changed", {
			previous_date: selectedDateKey,
			new_date: newDate.toFormat("yyyy-MM-dd"),
		});
		selectDate(newDate);
	};

	const handleOpenChange = useCallback(
		(isOpen: boolean) => {
			setIsCalendarOpen(isOpen);
			track("nurse_schedule_calendar_toggle", { is_open: isOpen });
		},
		[track],
	);

	const openShifts = allOpenShifts ?? {};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", gap: 2 }}>
			{isDynamicWeekStartEnabled && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
					<WeekStartDaySelector source="staffSchedule" />
				</Box>
			)}
			<CustomCalendar
				shifts={shifts}
				onRootHeightChange={setCalendarHeaderHeight}
				value={selection.date}
				onChange={handleCalendarChange}
				isOpen={isCalendarOpen}
				onOpenChange={handleOpenChange}
				showOneWeek={false}
				allowWeeksExpansion={true}
				pager={pager}
			/>

			<Box
				ref={scrollRef}
				onScroll={listScrollHandlers.onScroll}
				onScrollEnd={listScrollHandlers.onScrollEnd}
				id={"scrollable-nurse-schedule-container"}
				style={{
					overflowY: "auto",
					overscrollBehaviorY: "contain",
					WebkitOverflowScrolling: "touch",
					background: NEUTRAL[100],
				}}
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: `calc(100% - ${calendarHeaderHeight}px)`,
					position: "relative",
					p: 1,
					py: 0,
				}}
			>
				{onRefresh && <PullToRefresh childrenContainerEl={childrenContainerRef.current} scrollerEl={scrollRef.current} onRefresh={onRefresh} />}
				<Box ref={childrenContainerRef} id="nurse-schedule-content-box" data-testid="nurse-schedule-shift-list">
					{isMedicalDepartment ? (
						<ShiftWeekListMedical shifts={pageShifts} allOpenShifts={openShifts} staffScheduleDisplayUntilDate={staffScheduleDisplayUntilDate} />
					) : (
						<ShiftWeekList
							shifts={pageShifts}
							selectedDate={selectedDateKey}
							onDateSelect={handleDateSelect}
							expandedShiftIds={expandedShiftIds}
							onExpandOpenShift={handleExpandClick}
							allOpenShifts={openShifts}
							staffScheduleDisplayUntilDate={staffScheduleDisplayUntilDate}
						/>
					)}
					<ScheduleListFooter pager={pager} lastDay={pagerBounds?.lastDay} />
					{children}
				</Box>
			</Box>
		</Box>
	);
};
